import type { ChangeEvent } from "react"
import { useMemo, useRef, useState } from "react"
import {
	closestCenter,
	DndContext,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { useQuery } from "@rocicorp/zero/react"
import {
	IconGripDotsVerticalOutline18 as GripVertical,
	IconPlusOutline18 as Plus,
	IconTrash2Outline18 as Trash2,
	IconArrowUpFromLineOutline18 as Upload,
} from "nucleo-ui-outline-18"

import type { Membership, User, WidgetContactAvatar } from "@productlane/zero"
import {
	Avatar,
	Button,
	IconButton,
	Loader,
	Popover,
	Switch,
	Tooltip,
} from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { DragEndEvent } from "@dnd-kit/core"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { generateId } from "@/lib/id"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"

type WidgetContactAvatarWithMembership = WidgetContactAvatar & {
	membership:
		| (Membership & { user: User | null | undefined })
		| null
		| undefined
}

interface AvatarUploadProps {
	avatarId: string
	displayImage: string | null
	displayName: string
	onUpload: (avatarId: string, url: string) => Promise<void>
}

function AvatarUpload({
	avatarId,
	displayImage,
	displayName,
	onUpload,
}: AvatarUploadProps) {
	const fileInputRef = useRef<HTMLInputElement | null>(null)
	const [preview, setPreview] = useState<string | null>(null)
	const [isUploading, setIsUploading] = useState(false)
	const getSignedUrlMutation = api.file.getUrl.useMutation()

	const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (!file) return
		setPreview(URL.createObjectURL(file))
		setIsUploading(true)
		try {
			const { fileUrl, uploadUrl } = await getSignedUrlMutation.mutateAsync({
				fileName: file.name,
				fileType: file.type,
			})
			await fetch(uploadUrl, {
				method: "PUT",
				body: file,
				headers: { "Content-Type": file.type },
			})
			await onUpload(avatarId, fileUrl)
		} catch {
			setPreview(null)
			toast.error({ title: "Failed to upload image" })
		} finally {
			setIsUploading(false)
			if (fileInputRef.current) fileInputRef.current.value = ""
		}
	}

	const src = preview ?? displayImage ?? ""

	return (
		<>
			{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
			<div
				className="group relative h-8 w-8 flex-shrink-0"
				onClick={() => fileInputRef.current?.click()}
			>
				<Avatar name={displayName} image={src || null} size="sm" />
				<div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
					{isUploading ? (
						<Loader size={4} color="#fff" />
					) : (
						<Upload className="h-3.5 w-3.5 text-white" />
					)}
				</div>
			</div>
			<input
				ref={fileInputRef}
				type="file"
				accept="image/*"
				className="hidden"
				onChange={(e) => void handleFileChange(e)}
			/>
		</>
	)
}

function SortableAvatarRow({
	avatar,
	onRemove,
	onUpload,
}: {
	avatar: WidgetContactAvatarWithMembership
	onRemove: (id: string) => Promise<void>
	onUpload: (id: string, url: string) => Promise<void>
}) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: avatar.id })

	const displayImage =
		avatar.overrideImageUrl ?? avatar.membership?.user?.imageUrl ?? null
	const displayName = avatar.membership?.user?.name ?? ""

	return (
		<div
			ref={setNodeRef}
			style={{
				transform: CSS.Transform.toString(transform),
				transition,
				opacity: isDragging ? 0.5 : 1,
			}}
			className="group/widget-avatar flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-850"
		>
			<button
				type="button"
				className="flex-shrink-0 touch-none text-tertiary"
				{...attributes}
				{...listeners}
			>
				<GripVertical className="h-4 w-4" />
			</button>
			<AvatarUpload
				avatarId={avatar.id}
				displayImage={displayImage}
				displayName={displayName}
				onUpload={onUpload}
			/>
			<div className="flex flex-1 flex-col truncate">
				<span className="truncate text-sm font-medium">{displayName}</span>
				{avatar.overrideImageUrl && (
					<span className="text-xxs text-tertiary">Image overridden</span>
				)}
			</div>
			<Tooltip label="Remove member from widget">
				<IconButton
					Icon={Trash2}
					variant="ghost"
					onClick={() => void onRemove(avatar.id)}
					className="opacity-0 group-hover/widget-avatar:opacity-100"
				/>
			</Tooltip>
		</div>
	)
}

interface ContactAvatarsSectionProps {
	isContactAvatarsEnabled: boolean
	onToggle: (checked: boolean) => void
}

export function ContactAvatarsSection({
	isContactAvatarsEnabled,
	onToggle,
}: ContactAvatarsSectionProps) {
	const zero = useZero()
	const workspace = useZeroWorkspace()

	const [addOpen, setAddOpen] = useState(false)

	const [avatars] = useQuery(queries.widgetContactAvatars())
	const [memberships] = useQuery(queries.memberships())

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	)

	const handleDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event
		if (!over || active.id === over.id) return
		const oldIndex = avatars.findIndex((a) => a.id === active.id)
		const newIndex = avatars.findIndex((a) => a.id === over.id)
		const reordered = arrayMove([...avatars], oldIndex, newIndex)
		await Promise.all(
			reordered.map((avatar, index) =>
				mutateOk(
					zero.mutate(
						mutators.widgetContactAvatars.update({
							id: avatar.id,
							order: index,
						}),
					),
				),
			),
		)
	}

	const selectedMembershipIds = useMemo(
		() => new Set(avatars.map((a) => a.membershipId)),
		[avatars],
	)

	const availableMemberships = useMemo(
		() =>
			memberships.filter(
				(m) => m.role !== "VIEWER" && !selectedMembershipIds.has(m.id),
			),
		[memberships, selectedMembershipIds],
	)

	const handleAddMember = async (membershipId: string) => {
		const nextOrder = avatars.length
		if (!workspace?.widgetSettings?.id) {
			toast.error({
				title: "Widget settings not found",
			})
			return
		}
		await mutateOk(
			zero.mutate(
				mutators.widgetContactAvatars.create({
					id: generateId(),
					widgetSettingsId: workspace.widgetSettings.id,
					membershipId,
					order: nextOrder,
				}),
			),
		)
		setAddOpen(false)
	}

	const handleRemove = async (avatarId: string) => {
		await mutateOk(
			zero.mutate(mutators.widgetContactAvatars.delete({ id: avatarId })),
		)
	}

	const handleOverrideUpload = async (avatarId: string, url: string) => {
		await mutateOk(
			zero.mutate(
				mutators.widgetContactAvatars.update({
					id: avatarId,
					overrideImageUrl: url,
				}),
			),
		)
	}

	return (
		<>
			<div className="flex items-center justify-between">
				<div>
					<p className="text-sm font-medium">Contact avatars</p>
					<p className="text-xs text-secondary">
						Show who&apos;s available to help.
					</p>
				</div>
				<Switch
					name="isContactAvatarsEnabled"
					checked={isContactAvatarsEnabled}
					onCheckedChange={onToggle}
				/>
			</div>

			{isContactAvatarsEnabled && (
				<div className="mt-3 space-y-2">
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						modifiers={[restrictToVerticalAxis]}
						onDragEnd={(e) => void handleDragEnd(e)}
					>
						<SortableContext
							items={avatars.map((a) => a.id)}
							strategy={verticalListSortingStrategy}
						>
							{avatars.map((avatar) => (
								<SortableAvatarRow
									key={avatar.id}
									avatar={avatar}
									onRemove={handleRemove}
									onUpload={handleOverrideUpload}
								/>
							))}
						</SortableContext>
					</DndContext>

					{avatars.length < 3 && availableMemberships.length > 0 && (
						<Popover
							open={addOpen}
							onOpenChange={setAddOpen}
							className="p-0"
							content={
								<Command
									filter={(_, search, keywords) =>
										keywords?.find((k) =>
											k
												.toLocaleLowerCase()
												.includes(search.toLocaleLowerCase()),
										)
											? 1
											: 0
									}
								>
									<CommandInput autoFocus placeholder="Search members..." />
									<CommandList>
										<CommandEmpty>No members found.</CommandEmpty>
										<CommandGroup>
											{availableMemberships.map((m) => (
												<CommandItem
													key={m.id}
													value={m.id}
													keywords={m.user?.name ? [m.user.name] : []}
													onSelect={() => void handleAddMember(m.id)}
												>
													<div className="flex items-center gap-2">
														<Avatar
															image={m.user?.imageUrl ?? null}
															name={m.user?.name ?? ""}
															size="xs"
														/>
														<span className="truncate">{m.user?.name}</span>
													</div>
												</CommandItem>
											))}
										</CommandGroup>
									</CommandList>
								</Command>
							}
						>
							<Button type="button" variant="outline" size="sm" Icon={Plus}>
								Add member
							</Button>
						</Popover>
					)}
				</div>
			)}
		</>
	)
}
